import { FC, useMemo, useCallback, useContext, createContext } from 'react';
import { useAsync } from 'react-use';
import { BehavioralInsightPresentationMode, EventTypes } from 'modules/domains/segmentation';
import noop from 'utils/noop';
import { useFetchCustomEvents } from 'utils/hooks/useCustomEvents';
import { ITERABLE_EVENTS } from 'constants/eventTypes';
import {
  useAddCustomBehavioralInsight,
  useDeleteCustomBehavioralInsight,
  useCustomBehavioralInsightsQuery,
} from '../../hooks';
import { getBehavioralEventLabel } from '../../utils';
import { useBehaviorQueryContext } from './behaviorQuery';
import { CustomBehavioralInsight, BehaviorQuery, BehavioralEvent } from '../../types';
import { ORGANIZED_STANDARD_ITERABLE_EVENTS } from '../../constants';

export type CustomBehavioralContext = {
  addCustomBehavioralEvent: (
    eventName: string,
    mode: BehavioralInsightPresentationMode,
    dataType: EventTypes,
  ) => void;
  deleteCustomBehavioralEvent: (eventName: string, dataType: EventTypes) => void;
  cachedCustomBehavioralInsights: CustomBehavioralInsight[];
  selectableEventsForProject: BehavioralEvent[];
  allCustomEvents: BehavioralEvent[];
  allStandardEvents: BehavioralEvent[];
  maxBehavioralInsightSettingsForProject: number;
};

export const customBehavioralDefaultState = {
  addCustomBehavioralEvent: noop,
  deleteCustomBehavioralEvent: noop,
  cachedCustomBehavioralInsights: [],
  selectableEventsForProject: [],
  allCustomEvents: [],
  allStandardEvents: [],
  maxBehavioralInsightSettingsForProject: 0,
};

export const CustomBehavioralContext = createContext<CustomBehavioralContext>(
  customBehavioralDefaultState,
);
export const useCustomBehavioralContext = () => useContext(CustomBehavioralContext);

export const CustomBehavioralContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const customEvents = (useFetchCustomEvents() || []).map((event) => event.fieldName);
  const { behavior, refetchBehavior } = useBehaviorQueryContext();
  const { addCustomBehavioralInsight, isSuccess: customInsightAdded } =
    useAddCustomBehavioralInsight();
  const { deleteCustomBehavioralInsight, isSuccess: customInsightDeleted } =
    useDeleteCustomBehavioralInsight();
  const {
    data: { settings = [], maxBehavioralInsightSettingsForProject = 0 } = {},
    refetch: refetchCustomInsights,
  } = useCustomBehavioralInsightsQuery();

  /** all custom events organized by eventName and dataType and sorted alphabetically */
  const allCustomEvents: BehavioralEvent[] = useMemo(
    () =>
      customEvents
        .map((customEvent: string) => ({
          event: customEvent,
          dataType: EventTypes.CustomEvent,
        }))
        .sort((a: BehavioralEvent, b: BehavioralEvent) => a.event.localeCompare(b.event)),
    [customEvents],
  );

  /** all events in project excluding current behavioral events */
  const allEventsForProject: BehavioralEvent[] = useMemo(
    () => [...allCustomEvents, ...ORGANIZED_STANDARD_ITERABLE_EVENTS],
    [allCustomEvents],
  );

  useAsync(async () => {
    if (customInsightAdded || customInsightDeleted) {
      await refetchCustomInsights();
      await refetchBehavior();
    }
  }, [customInsightAdded, customInsightDeleted]);

  const cachedCustomBehavioralInsights = useMemo(
    () =>
      settings
        .map(({ eventName, presentationMode, dataType }) => {
          const event = getBehavioralEventLabel(dataType, ITERABLE_EVENTS, eventName);
          return {
            event,
            behavior: behavior[event as keyof BehaviorQuery],
            eventName,
            mode: presentationMode,
            dataType,
          };
        })
        .reverse(),
    [behavior, settings],
  );

  /** all current custom behavioral events by name */
  const currentEvents = cachedCustomBehavioralInsights.map(({ event }) => event);

  const selectableEventsForProject: BehavioralEvent[] = allEventsForProject.filter(
    ({ event }) => !currentEvents.includes(event),
  );

  const addCustomBehavioralEvent = useCallback(
    (event: string, mode: BehavioralInsightPresentationMode, dataType: EventTypes) => {
      addCustomBehavioralInsight(event, mode, dataType);
    },
    [addCustomBehavioralInsight],
  );

  const deleteCustomBehavioralEvent = useCallback(
    (event: string, dataType: EventTypes) => {
      deleteCustomBehavioralInsight(event, dataType);
    },
    [deleteCustomBehavioralInsight],
  );

  const value = {
    addCustomBehavioralEvent,
    deleteCustomBehavioralEvent,
    cachedCustomBehavioralInsights,
    selectableEventsForProject,
    allCustomEvents,
    allStandardEvents: ORGANIZED_STANDARD_ITERABLE_EVENTS,
    maxBehavioralInsightSettingsForProject,
  };

  return (
    <CustomBehavioralContext.Provider value={value}>{children}</CustomBehavioralContext.Provider>
  );
};
